import { isNotEmpty, isDefined, isSimpleDateFormat } from "utils/validation"
import { normalizeBoolean, convertToBoolean } from "utils/functions"
import dateService from "services/date"
import {
	partnerChainList,
	genderMFCList,
	MFC_OPERATOR,
	BC_OPERATOR,
	MC_OPERATOR,
	INACTIVE_OPERATOR_STATUS,
	ACTIVE_OPERATOR_STATUS,
	COLLECTIVITE_GENDER,
	VELOCISTE_GENDER,
	AM_GENDER,
	HORS_AM_GENDER,
} from "utils/constants"

export const prepareDataToPut = ({
	isUpdate = false,
	partner,
	isAdminMFC = false,
	isAdminBicycode = false,
	isAdminMoustache = false,
	isPartner = false,
}) => {
	const adresse = {
		numero_et_voie: partner.numero_et_voie,
		code_postal: partner.code_postal,
		ville: partner.ville,
		code_insee: partner.code_insee,
		pays: partner.pays,
	}

	const data_operators = []
	let registermailtemplate = BC_OPERATOR

	// Moustache partner
	// -------------------
	if (partner.isMoustache) {
		const operatorMoustache = {
			entity_data: {},
			status: ACTIVE_OPERATOR_STATUS,
			operator: MC_OPERATOR,
		}

		data_operators.push(operatorMoustache)
		registermailtemplate = MC_OPERATOR
	} else {
		const existingMC = partner?.data_operators?.find(
			(oneOperator) => oneOperator.operator === MC_OPERATOR
		)
		if (isUpdate === true && isDefined(existingMC)) {
			// Inactive status
			const operatorMC = {
				entity_data: existingMC.entity_data,
				status: INACTIVE_OPERATOR_STATUS,
				operator: MC_OPERATOR,
			}

			data_operators.push(operatorMC)
		}
	}

	// MFC partner
	// -------------------
	if (partner.isMFC) {
		const entity_data = {
			operator_gender: genderMFCList.find(
				(oneGender) => oneGender.value === partner.gender_mfc
			).id_api,
			...(isNotEmpty(partner.mfc_client_number)
				? { operator_store_id: partner.mfc_client_number }
				: {}),
			...(isNotEmpty(partner.mfc_account_id)
				? { account_id: String(partner.mfc_account_id) }
				: {}),
			...(isNotEmpty(partner.mfc_is_fleet)
				? {
						is_fleet: convertToBoolean(partner.mfc_is_fleet),
				  }
				: {}),
		}

		const operatorMFC = {
			entity_data: entity_data,
			operator: MFC_OPERATOR,
			status: ACTIVE_OPERATOR_STATUS,
		}

		data_operators.push(operatorMFC)
		registermailtemplate = MFC_OPERATOR
	} else {
		const existingMFC = partner?.data_operators?.find(
			(oneOperator) => oneOperator.operator === MFC_OPERATOR
		)
		if (isUpdate === true && isDefined(existingMFC)) {
			// Inactive status
			const operatorMFC = {
				entity_data: existingMFC.entity_data,
				status: INACTIVE_OPERATOR_STATUS,
				operator: MFC_OPERATOR,
			}

			data_operators.push(operatorMFC)
		}
	}

	// BICYCODE partner
	// -------------------
	if (partner.isBicycode) {
		const entity_data = {
			is_member_fub: normalizeBoolean(partner.isFub),
			...(isNotEmpty(partner.bc_iban) ? { iban: partner.bc_iban } : {}),
			...(isNotEmpty(partner.bc_bic) ? { bic: partner.bc_bic } : {}),
			...(isNotEmpty(partner.bc_bank_name) ? { bank_name: partner.bc_bank_name } : {}),
			...(isNotEmpty(partner.bc_effective_date)
				? { effective_date: partner.bc_effective_date }
				: {}),
			...(isNotEmpty(partner.bc_rum) ? { rum: partner.bc_rum } : {}),
			...(isNotEmpty(partner.invoicing_agent_mail)
				? { invoicing_agent_mail: partner.invoicing_agent_mail }
				: {}),
			...(isNotEmpty(partner.invoicing_agent_firstname)
				? { invoicing_agent_firstname: partner.invoicing_agent_firstname }
				: {}),
			...(isNotEmpty(partner.invoicing_agent_lastname)
				? { invoicing_agent_lastname: partner.invoicing_agent_lastname }
				: {}),
			...(isNotEmpty(partner.bc_account_id)
				? { account_id: String(partner.bc_account_id) }
				: {}),
			...(isNotEmpty(partner.bc_is_fleet)
				? {
						is_fleet: convertToBoolean(partner.bc_is_fleet),
				  }
				: {}),
		}

		const operatorBicycode = {
			entity_data: entity_data,
			operator: BC_OPERATOR,
			status: ACTIVE_OPERATOR_STATUS,
		}

		data_operators.push(operatorBicycode)
		registermailtemplate = BC_OPERATOR
	} else {
		const existingBC = partner?.data_operators?.find(
			(oneOperator) => oneOperator.operator === BC_OPERATOR
		)
		if (isUpdate === true && isDefined(existingBC)) {
			// Inactive status
			const operatorBC = {
				entity_data: existingBC.entity_data,
				status: INACTIVE_OPERATOR_STATUS,
				operator: BC_OPERATOR,
			}

			data_operators.push(operatorBC)
		}
	}

	var registerDate
	if (partner.gender === AM_GENDER) {
		// We test the date format. Then, we format the new date to send it
		const currentRegisterDate = isSimpleDateFormat(partner.registre_date)
			? new Date(partner.registre_date)
			: partner.registre_date

		registerDate = dateService.formatDate(currentRegisterDate, "dd/MM/y")
	}

	const entity_data = {
		gender: parseInt(partner.gender),

		// Le siret porte différentes info en fonction du gender de l'entité
		// Le siret pour un vélociste est obligatoire si le pays sélectionné est France
		...((partner.gender === COLLECTIVITE_GENDER || partner.gender === VELOCISTE_GENDER) &&
		partner.siret
			? { siret: partner.siret }
			: {}), // Velocistes / Collectivites
		...(partner.gender === HORS_AM_GENDER ? { siret: "WWWWW" + partner.rna } : {}), // hors Alsace Moselle
		...(partner.gender === AM_GENDER
			? {
					siret:
						registerDate.replace(/\//g, "") +
						partner.registre_volume +
						partner.registre_folio,
			  }
			: {}), // Alsace Moselle

		code_ape: partner.code_ape,
		raison_sociale: partner.raison_sociale,
		nom_commercial: partner.nom_commercial,
		nom_representant_legal: partner.nom_representant_legal,
		prenom_representant_legal: partner.prenom_representant_legal,
		mail: partner.mail,
		telephone_1: partner.telephone_1,
		telephone_2: partner.telephone_2,
		adresse: adresse,
		horaires_ouverture: partner.workingHours,
		est_une_chaine: normalizeBoolean(partner.isFranchise),
		est_un_fabricant: normalizeBoolean(partner.isMaker),
		import_allowed: partner.import_allowed,
		est_bicytrust: partner.est_bicytrust,
		...(partner.tva_intra ? { tva_intra: partner.tva_intra } : {}),
	}

	// Optional fields
	if (isNotEmpty(partner.wallet_id)) entity_data.wallet_id = partner.wallet_id
	if (isNotEmpty(partner.site_web)) entity_data.site_web = partner.site_web
	if (partner.latitude) entity_data.latitude = parseFloat(partner.latitude)
	if (partner.longitude) entity_data.longitude = parseFloat(partner.longitude)

	if (entity_data.est_une_chaine) {
		const chaine = partnerChainList.find(
			(onePartnerChain) => onePartnerChain.value === partner.structureChainName
		)
		if (isNotEmpty(chaine)) {
			entity_data.code_chaine = chaine.value
			entity_data.nom_chaine = chaine.label
		}
	}

	// Format final return
	let newPartner = {}
	if (isUpdate) {
		newPartner = {
			entity_id: partner.entity_id,
			last_row_hash: partner.row_hash,
			entity_data: entity_data,
			...(() => {
				if (!isPartner) {
					return {
						data_operators: data_operators,
					}
				}
			})(),
			mailtemplate: registermailtemplate,
		}
	} else {
		newPartner = {
			entity_data,
			...(() => {
				if (!isPartner) {
					return {
						data_operators: data_operators,
					}
				}
			})(),
			registermailtemplate: registermailtemplate,
		}
	}

	return newPartner
}
